import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import IntelligencePanel from "@/components/IntelligencePanel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { DownloadAnswerButton } from "@/components/DownloadAnswerButton";
import { downloadEssayPdf } from "@/lib/answerPdf";
import PaywallNotice from "@/components/PaywallNotice";
import HandwrittenUpload from "@/components/HandwrittenUpload";
import EssayPYQBank from "@/components/essay/EssayPYQBank";
import {
  FileText, Sparkles, Target, TrendingUp, Eye, Download, 
  BookOpen, Lightbulb, BarChart3, CheckCircle2, AlertCircle, 
  Award, Zap, ArrowRight, RefreshCw, Camera, ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const EssaySuitePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mode, setMode] = useState("home"); // home, predict, write, evaluate, history
  const [essayInputMode, setEssayInputMode] = useState("type"); // "type" or "handwritten"
  const [loading, setLoading] = useState(false);
  
  // Prediction state
  const [predictedTopics, setPredictedTopics] = useState([]);
  const [pyqAnalysis, setPyqAnalysis] = useState(null);
  const [caMapping, setCaMapping] = useState(null);
  const [upscTwists, setUpscTwists] = useState([]);
  const [targetExam, setTargetExam] = useState(null);
  
  // Writing state
  const [selectedTopic, setSelectedTopic] = useState("");
  const [customTopic, setCustomTopic] = useState("");
  const [wordLimit, setWordLimit] = useState(1200);
  const [philosophicalDepth, setPhilosophicalDepth] = useState("medium");
  const [generatedEssay, setGeneratedEssay] = useState(null);
  
  // Evaluation state
  const [evaluationTopic, setEvaluationTopic] = useState("");
  const [userEssay, setUserEssay] = useState("");
  const [evaluation, setEvaluation] = useState(null);
  
  // History
  const [predictionHistory, setPredictionHistory] = useState([]);
  const [essayHistory, setEssayHistory] = useState([]);
  const [evaluationHistory, setEvaluationHistory] = useState([]);
  
  // Essay usage tracking for FREE users (per lifetime, not daily)
  const [essayUsage, setEssayUsage] = useState({ 
    is_pro: false, 
    suite: "ESSAY",
    free_full_essay_used: false, 
    essay_type: "full",
    message: ""
  });
  
  // Evaluation usage tracking for FREE users (per lifetime)
  const [evalUsage, setEvalUsage] = useState({
    is_pro: false,
    free_full_evaluation_used: false,
    evaluation_type: "full",
    message: ""
  });

  const isPro = user?.role === "PRO";
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!isPro) {
      toast({ description: "Saving essays as PDF is a PRO feature. Upgrade to download." });
      return;
    }
    setDownloadingPdf(true);
    try {
      const topic = selectedTopic || customTopic;
      await downloadEssayPdf({ topic, essay: generatedEssay });
      toast({ description: "PDF downloaded." });
    } catch (e) {
      toast({ variant: "destructive", description: "Could not generate PDF." });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Fetch essay and evaluation usage on mount
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const [essayRes, evalRes] = await Promise.all([
          managedAxios.get(`${API_BASE}/essay/usage`),
          managedAxios.get(`${API_BASE}/eval/usage`)
        ]);
        setEssayUsage(essayRes.data);
        setEvalUsage(evalRes.data);
      } catch (err) {
        console.error("Failed to fetch usage:", err);
      }
    };
    fetchUsage();
  }, []);

  const handlePredictTopics = async (numTopics = 10) => {
    setLoading(true);
    try {
      // Use longer timeout for comprehensive prediction (2 parallel LLM calls)
      const { data } = await managedAxios.post(`${API_BASE}/essay/predict`, {
        num_topics: isPro ? numTopics : 3,
        include_pyq_analysis: isPro,  // Full PYQ analysis for PRO users
        include_ca_mapping: isPro,    // Full CA mapping for PRO users
        target_exam: "August 2026"    // Target Mains exam
      }, {
        timeout: 180000,  // 3 minutes timeout for comprehensive analysis
        signal: undefined  // Disable abort controller for this long request
      });
      
      setPredictedTopics(data.topics || []);
      setPyqAnalysis(data.pyq_analysis);
      setCaMapping(data.ca_mapping);
      setUpscTwists(data.upsc_twist_examples || data.upsc_twists || []);
      setTargetExam(data.target_exam);
      
      if (data.message) {
        toast({ description: data.message, duration: 5000 });
      } else {
        toast({ description: `✓ Predicted ${data.topics?.length || 0} essay topics for ${data.target_exam || 'upcoming'} Mains!` });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not predict topics. Please try again.";
      toast({ variant: "destructive", description: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateEssay = async () => {
    const topic = selectedTopic || customTopic;
    if (!topic) {
      toast({ variant: "destructive", description: "Please enter or select a topic" });
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/essay/write`, {
        topic: topic,
        word_limit: wordLimit,
        include_diagrams: isPro,
        philosophical_depth: isPro ? philosophicalDepth : "medium"
      });
      
      // Show the generated essay FIRST
      setGeneratedEssay(data);
      
      if (data.message) {
        toast({ description: data.message, duration: 5000 });
      } else {
        toast({ description: "✓ Essay generated successfully!" });
      }
      
      // For FREE users: Update usage state after generation
      if (!isPro && data.usage) {
        setTimeout(() => {
          setEssayUsage({
            is_pro: false,
            suite: "ESSAY",
            free_full_essay_used: data.usage.free_full_essay_used || true,
            essay_type: data.usage.essay_type || "partial",
            message: data.paywall_message || ""
          });
          if (data.paywall_message) {
            toast({ 
              description: data.paywall_message,
              duration: 5000
            });
          }
        }, 10000); // 10 seconds delay
      } else if (data.usage) {
        // PRO users: reflect the live billing-cycle quota returned by the backend
        const q = data.quota;
        setEssayUsage((prev) => ({
          ...prev,
          is_pro: true,
          suite: "ESSAY",
          free_full_essay_used: false,
          essay_type: "full",
          unlimited: q ? false : (prev.unlimited ?? false),
          limit: q?.limit ?? prev.limit,
          remaining: q?.remaining ?? prev.remaining,
          resets_on: q?.resets_on ?? prev.resets_on,
          message: data.paywall_message || (q ? `${q.remaining}/${q.limit} essays left this cycle` : "Unlimited full essays"),
        }));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not generate essay";
      toast({ variant: "destructive", description: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluateEssay = async () => {
    if (!evaluationTopic || !userEssay) {
      toast({ variant: "destructive", description: "Please provide topic and essay" });
      return;
    }

    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/essay/evaluate`, {
        topic: evaluationTopic,
        essay_text: userEssay,
        expected_word_limit: 1200
      });

      // Guard against a malformed/incomplete evaluation payload — show a clear
      // retry prompt instead of a blank "/125" score card.
      if (data?.error || data?.total_score === null || data?.total_score === undefined) {
        toast({
          variant: "destructive",
          description: data?.message || "We couldn't score this essay just now. Please click Evaluate again.",
        });
        return;
      }

      setEvaluation(data);
      toast({ description: "✓ Essay evaluated successfully!" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not evaluate essay";
      toast({ variant: "destructive", description: msg });
    } finally {
      setLoading(false);
    }
  };

  // Home View
  if (mode === "pyq") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-pink-50/30 to-rose-50/30">
        <EssayPYQBank
          onBack={() => setMode("home")}
          onPractice={(topic) => {
            setEvaluationTopic(topic || "");
            setEssayInputMode("type");
            setMode("evaluate");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    );
  }

  if (mode === "home") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-pink-50/30 to-rose-50/30">
        
        {/* ═══════════════════════════════════════════════════════════
            VIBRANT HEADER - Pink/Rose Theme for Essay
            ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-fuchsia-500 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            {/* Back button & Title */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => navigate("/mains")}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Sparkles className="w-7 h-7" />
                    Essay Suite
                  </h1>
                  <p className="text-pink-100 text-sm">Predict • Write • Evaluate</p>
                </div>
              </div>
              
              {/* PRO Badge */}
              <Badge className={`px-3 py-1.5 ${isPro 
                ? 'bg-white/20 text-white border-0' 
                : 'bg-white/10 text-pink-100 border-0'}`}>
                {isPro ? '⭐ PRO' : '🆓 FREE'}
              </Badge>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? (essayUsage.unlimited ? '∞' : (essayUsage.remaining ?? essayUsage.limit ?? 10)) : essayUsage.free_full_essay_used ? '0' : '1'}</p>
                <p className="text-[10px] text-pink-200">Full Essays</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? '10+' : '🔒'}</p>
                <p className="text-[10px] text-pink-200">Predictions</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">/125</p>
                <p className="text-[10px] text-pink-200">Scoring</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          
          {/* ═══════════════════════════════════════════════════════════
              TOPPER TIP BANNER
              ═══════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-pink-100 to-rose-100 overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-pink-900">Essay Strategy</p>
                  <p className="text-sm text-pink-700">Aim for 125+ marks: Strong intro, balanced dimensions, philosophical conclusion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ═══════════════════════════════════════════════════════════
              THREE MAIN MODES
              ═══════════════════════════════════════════════════════════ */}
          <div className="space-y-4">
            {/* Essay Prediction - PRO Only */}
            <Card 
              className={`border-2 cursor-pointer transition-all overflow-hidden ${
                isPro 
                  ? 'border-indigo-200 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 hover:shadow-xl hover:scale-[1.01]' 
                  : 'border-slate-200 bg-slate-50/50'
              } group`}
              onClick={() => {
                if (!isPro) {
                  navigate("/upgrade");
                  return;
                }
                setMode("predict");
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform ${
                    isPro ? 'bg-gradient-to-br from-indigo-500 to-blue-500' : 'bg-slate-400'
                  }`}>
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Essay Prediction</h3>
                      {!isPro && <Badge className="bg-slate-200 text-slate-600">🔒 PRO</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {isPro ? "10-20 predicted topics with probabilities, PYQ analysis & CA mapping" : "AI-predicted topics based on 20+ years of patterns"}
                    </p>
                  </div>
                  <ArrowRight className={`w-5 h-5 ${isPro ? 'text-indigo-500' : 'text-slate-400'}`} />
                </div>
              </CardContent>
            </Card>

            {/* Essay Writing */}
            <Card 
              className="border-2 border-purple-200 bg-gradient-to-br from-purple-500/10 to-fuchsia-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.01] transition-all group overflow-hidden"
              onClick={() => setMode("write")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <FileText className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Essay Writing</h3>
                      {!isPro && (
                        <Badge className={`text-xs ${!essayUsage.free_full_essay_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {!essayUsage.free_full_essay_used ? '1 FREE' : 'Partial'}
                        </Badge>
                      )}
                    {isPro && !essayUsage.unlimited && typeof essayUsage.remaining === 'number' && (
                        <Badge className={`text-xs ${essayUsage.remaining > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {essayUsage.remaining}/{essayUsage.limit || 10} left
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {isPro ? (essayUsage.unlimited ? "Unlimited essays with diagrams, data, quotes & topper voice" : `${essayUsage.remaining ?? essayUsage.limit ?? 10}/${essayUsage.limit || 10} full essays left this cycle`) : `${!essayUsage.free_full_essay_used ? '1 free full essay available' : 'Unlimited partial essays'}`}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            {/* Essay Evaluation */}
            <Card 
              className="border-2 border-rose-200 bg-gradient-to-br from-rose-500/10 to-pink-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.01] transition-all group overflow-hidden"
              onClick={() => setMode("evaluate")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Essay Evaluation</h3>
                      {!isPro && (
                        <Badge className={`text-xs ${!evalUsage.free_full_evaluation_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {!evalUsage.free_full_evaluation_used ? '1 FREE' : 'Partial'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {isPro ? "Get scored out of 125 with detailed feedback & improvement plan" : `${!evalUsage.free_full_evaluation_used ? '1 free full evaluation available' : 'Unlimited partial evaluations'}`}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-rose-500" />
                </div>
              </CardContent>
            </Card>

            {/* Essay PYQ Analysis */}
            <Card
              className="border-2 border-amber-200 bg-gradient-to-br from-amber-500/10 to-orange-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.01] transition-all group overflow-hidden"
              onClick={() => setMode("pyq")}
              data-testid="essay-pyq-open-card"
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Essay PYQ Analysis</h3>
                      <Badge className="text-xs bg-green-100 text-green-700">FREE</Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      14 years of essays (2013-2026) mapped into 18 themes with a full writing roadmap for each
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ═══════════════════════════════════════════════════════════
              FEATURES ROW
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-800">PYQ Trends</p>
                <p className="text-[10px] text-slate-500">20-30 years {!isPro && "🔒"}</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-800">CA Mapping</p>
                <p className="text-[10px] text-slate-500">Theme links {!isPro && "🔒"}</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-800">Topper Voice</p>
                <p className="text-[10px] text-slate-500">130+ style {!isPro && "🔒"}</p>
              </CardContent>
            </Card>
          </div>

          {/* PRO Upgrade CTA for FREE users */}
          {!isPro && (
            <Card 
              className="border-2 border-pink-300 bg-gradient-to-r from-pink-50 to-rose-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate("/upgrade")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold font-heading text-lg text-slate-800">Unlock Full Essay Intelligence</p>
                    <p className="text-sm text-slate-600">Get predictions, unlimited essays, detailed evaluation & more</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-pink-500" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Quote */}
          <div className="text-center pt-4 pb-8">
            <p className="text-sm text-slate-400 italic">&ldquo;Essays reveal your personality to UPSC. Make it count.&rdquo;</p>
          </div>
        </div>
      </div>
    );
  }

  // Prediction Mode
  if (mode === "predict") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">📊 Essay Topic Prediction</h1>
          <Button variant="outline" onClick={() => setMode("home")}>← Back</Button>
        </div>

        <Card className="border-indigo-200 bg-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Predict UPSC Essay Topics</h3>
                <p className="text-sm text-slate-600">
                  {isPro 
                    ? "Get 10-20 topics with probabilities, PYQ analysis & CA mapping" 
                    : "Get 3 predicted topics (upgrade to PRO for detailed analysis)"}
                </p>
              </div>
              <Button onClick={() => handlePredictTopics(isPro ? 10 : 3)} disabled={loading}>
                {loading ? "Predicting..." : "Generate Predictions"}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Predicted Topics */}
        {predictedTopics.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Predicted Essay Topics</h2>
            {predictedTopics.map((topic, idx) => (
              <Card key={idx} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-indigo-600">#{idx + 1}</Badge>
                        {topic.probability && (
                          <Badge variant="outline" className="bg-emerald-50">
                            {Math.round(topic.probability)}% probability
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{topic.topic}</h3>
                      {topic.rationale && (
                        <p className="text-sm text-slate-600 mb-3">{topic.rationale}</p>
                      )}
                      {isPro && (topic.pyq_score || topic.ca_score) && (
                        <div className="flex gap-4 text-xs">
                          {topic.pyq_score && (
                            <span className="text-slate-600">PYQ Score: {topic.pyq_score}/10</span>
                          )}
                          {topic.ca_score && (
                            <span className="text-slate-600">CA Score: {topic.ca_score}/10</span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setSelectedTopic(topic.topic);
                        setMode("write");
                      }}
                    >
                      Write Essay
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* PYQ Analysis */}
        {isPro && pyqAnalysis && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">📈 PYQ Trend Analysis (20-30 Years)</h2>
            
            {/* Theme Frequency */}
            {pyqAnalysis.theme_frequency && (
              <Card className="border-indigo-200 bg-indigo-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-4">Theme Frequency Distribution</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(pyqAnalysis.theme_frequency).map(([theme, data]) => (
                      <div key={theme} className="bg-white p-4 rounded-lg border border-indigo-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-slate-900 capitalize">{theme}</span>
                          <Badge className="bg-indigo-600">
                            {typeof data === 'object' ? data.percentage : data}%
                          </Badge>
                        </div>
                        {typeof data === 'object' && (
                          <>
                            <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                              <div 
                                className="bg-indigo-600 h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-600">Trend: {data.trend}</span>
                              {data.notable_years && (
                                <span className="text-slate-500">
                                  Years: {data.notable_years.join(', ')}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ideological Shifts */}
            {pyqAnalysis.ideological_shifts && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">🔄 Ideological Shifts (Decade-wise)</h3>
                  <div className="space-y-4">
                    {pyqAnalysis.ideological_shifts.map((shift, idx) => (
                      <div key={idx} className="border-l-4 border-purple-400 pl-4 py-2">
                        <h4 className="font-bold text-purple-900">{shift.period}</h4>
                        <p className="text-sm text-slate-700 mt-1">
                          <span className="font-semibold">Focus:</span> {shift.focus}
                        </p>
                        <p className="text-sm text-slate-600">
                          <span className="font-semibold">Orientation:</span> {shift.orientation}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pattern Cycles */}
            {pyqAnalysis.pattern_cycles && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">🔁 Cyclical Patterns</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {pyqAnalysis.pattern_cycles.map((pattern, idx) => (
                      <div key={idx} className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <h4 className="font-semibold text-amber-900 mb-1">
                          {typeof pattern === 'object' ? pattern.theme : pattern}
                        </h4>
                        {typeof pattern === 'object' && (
                          <>
                            <p className="text-sm text-slate-700">
                              <span className="font-semibold">Recurs:</span> {pattern.recurrence}
                            </p>
                            <p className="text-xs text-slate-600 mt-1">{pattern.reason}</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* UPSC Tendencies */}
            {pyqAnalysis.upsc_tendencies && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-emerald-900 mb-4">💡 UPSC Examiner Tendencies</h3>
                  <ul className="space-y-2">
                    {pyqAnalysis.upsc_tendencies.map((tendency, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{tendency}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Emerging Themes */}
            {pyqAnalysis.emerging_themes_2024_2026 && (
              <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-rose-900 mb-4">🚀 Emerging Themes (2024-2026)</h3>
                  <div className="space-y-3">
                    {pyqAnalysis.emerging_themes_2024_2026.map((emerging, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-lg border border-rose-200">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-slate-900 flex-1">
                            {typeof emerging === 'object' ? emerging.theme : emerging}
                          </h4>
                          {typeof emerging === 'object' && emerging.probability && (
                            <Badge className="bg-rose-600">{emerging.probability}%</Badge>
                          )}
                        </div>
                        {typeof emerging === 'object' && emerging.rationale && (
                          <p className="text-xs text-slate-600">{emerging.rationale}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Red Flag Topics */}
            {pyqAnalysis.red_flag_topics && (
              <Card className="border-red-300 bg-red-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    ⚠️ Red Flag Topics (What UPSC Avoids)
                  </h3>
                  <ul className="space-y-2">
                    {pyqAnalysis.red_flag_topics.map((topic, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-red-800">
                        <span className="text-red-600">✗</span>
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Exam Strategy Insights */}
            {pyqAnalysis.exam_strategy_insights && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">🎯 Exam Strategy Insights</h3>
                  <ul className="space-y-2">
                    {pyqAnalysis.exam_strategy_insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                        <Zap className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* CA Mapping */}
        {isPro && caMapping && Array.isArray(caMapping) && caMapping.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">🗺️ Current Affairs → Essay Theme Mapping</h2>
            
            <div className="space-y-4">
              {caMapping.map((mapping, idx) => (
                <Card key={idx} className="border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-purple-100 p-3 rounded-lg">
                        <Lightbulb className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-purple-900 mb-3">{mapping.ca_area}</h3>
                        
                        {/* Recent Developments */}
                        {mapping.recent_developments && (
                          <div className="mb-4 bg-blue-50 p-3 rounded-lg">
                            <h4 className="text-sm font-semibold text-blue-900 mb-2">Recent Developments:</h4>
                            <ul className="space-y-1">
                              {mapping.recent_developments.map((dev, i) => (
                                <li key={i} className="text-xs text-blue-800">• {dev}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Essay Themes */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Potential Essay Themes:</h4>
                          <div className="space-y-2">
                            {(mapping.essay_themes || mapping.themes).map((theme, i) => (
                              <div key={i} className="flex items-start gap-2 bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200">
                                <Target className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">
                                    {typeof theme === 'object' ? theme.theme : theme}
                                  </p>
                                  {typeof theme === 'object' && theme.probability && (
                                    <Badge className="mt-1 bg-purple-600 text-xs">
                                      {theme.probability}% probability
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Rationale */}
                        {mapping.rationale && (
                          <div className="bg-amber-50 p-3 rounded-lg mb-3">
                            <p className="text-xs text-amber-900">
                              <span className="font-semibold">Why UPSC might ask:</span> {mapping.rationale}
                            </p>
                          </div>
                        )}
                        
                        {/* Multidimensional Angles */}
                        {mapping.multidimensional_angles && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-slate-700 mb-2">Multidimensional Angles:</h4>
                            <div className="grid md:grid-cols-2 gap-2">
                              {mapping.multidimensional_angles.map((angle, i) => (
                                <div key={i} className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                                  {angle}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Key Data Reports */}
                        {mapping.key_data_reports && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-slate-700 mb-2">Key Data/Reports to Use:</h4>
                            <div className="flex flex-wrap gap-2">
                              {mapping.key_data_reports.map((report, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{report}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Potential Quotes */}
                        {mapping.potential_quotes && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <h4 className="text-xs font-semibold text-green-900 mb-2">Potential Quotes:</h4>
                            <div className="space-y-1">
                              {mapping.potential_quotes.map((quote, i) => (
                                <p key={i} className="text-xs italic text-green-800">"{quote}"</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Cross-Cutting Themes */}
            {caMapping.cross_cutting_themes && (
              <Card className="border-indigo-200 bg-indigo-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-4">🔗 Cross-Cutting Themes</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {caMapping.cross_cutting_themes.map((theme, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-indigo-200">
                        <p className="text-sm text-slate-800">{theme}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strategic Insights */}
            {caMapping.strategic_insights && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-4">💡 Strategic Insights</h3>
                  <ul className="space-y-2">
                    {caMapping.strategic_insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* UPSC Twist Examples */}
        {isPro && upscTwists && upscTwists.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">✨ UPSC Twist Examples (Framing Styles)</h2>
            <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <CardContent className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                  Use these framing styles to add philosophical depth and UPSC-style sophistication to your essays:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {upscTwists.map((twist, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3">
                        <div className="bg-purple-100 p-2 rounded-full flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                        </div>
                        <p className="text-sm text-slate-800 italic">"{twist}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Target Exam Info */}
        {targetExam && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">🎯 Target Exam:</span> UPSC Mains {targetExam}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Writing Mode
  if (mode === "write") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">✍️ Essay Writing Engine</h1>
            {!isPro && (
              <Badge className={`text-sm px-3 py-1 ${!essayUsage.free_full_essay_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {!essayUsage.free_full_essay_used ? '1 FREE available' : 'Partial mode'}
              </Badge>
            )}
            {isPro && (
              <Badge className="bg-purple-100 text-purple-700 text-sm px-3 py-1">
                Unlimited
              </Badge>
            )}
          </div>
          <Button variant="outline" onClick={() => setMode("home")}>← Back</Button>
        </div>

        {/* Usage Alert for FREE users - only show AFTER they've used their essay */}
        {!isPro && essayUsage.free_full_essay_used && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-900">Partial Essay Mode</p>
                  <p className="text-sm text-amber-700">You&apos;ve used your 1 free full essay. You can still generate unlimited partial essays. Upgrade to PRO for unlimited full access!</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Topic Selection */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Essay Topic</label>
              <Textarea
                placeholder="Enter your essay topic (or select from predictions)"
                value={selectedTopic || customTopic}
                onChange={(e) => {
                  setSelectedTopic("");
                  setCustomTopic(e.target.value);
                }}
                rows={3}
              />
            </div>

            {/* Word limit for both FREE and PRO (FREE gets full essay now) */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Word Limit</label>
              <Input
                type="number"
                min="500"
                max={isPro ? 2000 : 1200}
                value={wordLimit}
                onChange={(e) => setWordLimit(parseInt(e.target.value))}
              />
              {!isPro && <p className="text-xs text-slate-500 mt-1">FREE: Max 1200 words • PRO: Up to 2000 words</p>}
            </div>

            {isPro && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Philosophical Depth</label>
                <select
                  value={philosophicalDepth}
                  onChange={(e) => setPhilosophicalDepth(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="low">Low (Basic)</option>
                  <option value="medium">Medium (Balanced)</option>
                  <option value="high">High (Deep philosophical)</option>
                </select>
              </div>
            )}

            <Button 
              className="w-full bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white"
              onClick={handleGenerateEssay} 
              disabled={loading}
            >
              {loading ? "Generating Essay..." : (!isPro && essayUsage.free_full_essay_used) ? "📋 Generate Partial Essay" : "Generate Full Essay"}
              {!loading && <Sparkles className="w-4 h-4 ml-2" />}
            </Button>
          </CardContent>
        </Card>

        {/* Generated Essay */}
        {generatedEssay && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">Model Essay</h2>
              <DownloadAnswerButton locked={!isPro} busy={downloadingPdf} onClick={handleDownloadPdf} testid="essay-download-pdf" />
            </div>
            {generatedEssay.outline && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">📝 Essay Outline</h2>
                  {generatedEssay.outline.map((section, idx) => (
                    <div key={idx} className="mb-4">
                      <h3 className="font-semibold text-slate-800">{section.subheading}</h3>
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        {section.points.map((point, i) => (
                          <li key={i} className="text-sm text-slate-600">{point}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {generatedEssay.deconstruction && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">🔍 Topic Deconstruction</h2>
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700">Keywords</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {generatedEssay.deconstruction.keywords?.map((kw, idx) => (
                          <Badge key={idx} variant="outline">{kw}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700">Dimensions</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {generatedEssay.deconstruction.dimensions?.map((dim, idx) => (
                          <Badge key={idx} variant="outline" className="bg-purple-50">{dim}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {generatedEssay.introductions && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">🚀 Three Introduction Styles</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-indigo-700 mb-1">Data/Report-Based</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.introductions.data_based}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-purple-700 mb-1">Anecdote/Story-Based</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.introductions.anecdote}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-rose-700 mb-1">Philosophical</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.introductions.philosophical}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {generatedEssay.diagram && (
              <Card className="bg-slate-50">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">📊 Essay Structure Diagram</h2>
                  <pre className="text-xs font-mono whitespace-pre overflow-x-auto">
                    {generatedEssay.diagram}
                  </pre>
                </CardContent>
              </Card>
            )}

            {generatedEssay.body && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">📚 Essay Body (Detailed)</h2>
                  <div className="space-y-6">
                    {generatedEssay.body.map((section, idx) => (
                      <div key={idx} className="border-l-4 border-indigo-300 pl-4">
                        <h3 className="font-bold text-slate-900 mb-2">
                          {section.subheading}
                          {section.keyword && <span className="text-indigo-600 ml-2">({section.keyword})</span>}
                        </h3>
                        {section.explanation && (
                          <p className="text-sm text-slate-700 mb-2">{section.explanation}</p>
                        )}
                        {section.example && (
                          <div className="bg-green-50 p-3 rounded mb-2">
                            <p className="text-xs font-semibold text-green-800 mb-1">Example:</p>
                            <p className="text-xs text-green-700">{section.example}</p>
                          </div>
                        )}
                        {section.data && (
                          <div className="bg-blue-50 p-3 rounded">
                            <p className="text-xs font-semibold text-blue-800 mb-1">Data/Report:</p>
                            <p className="text-xs text-blue-700">{section.data}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {generatedEssay.counterview && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">⚖️ Counterview / Debate</h2>
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-sm font-semibold text-green-700">Pro</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.counterview.pro}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-red-700">Con</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.counterview.con}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-purple-700">Middle Path</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.counterview.middle_path}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {generatedEssay.way_forward && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">🎯 Way Forward (Reforms)</h2>
                  <ul className="space-y-2">
                    {generatedEssay.way_forward.map((reform, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{reform}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {generatedEssay.conclusions && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">🏁 Three Conclusion Styles</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-indigo-700 mb-1">Inspirational</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.conclusions.inspirational}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-purple-700 mb-1">Constitutional/Value-Based</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.conclusions.constitutional}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-rose-700 mb-1">Philosophical/Civilizational</h3>
                      <p className="text-sm text-slate-700">{generatedEssay.conclusions.philosophical}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {generatedEssay.quotes && (
              <Card className="bg-amber-50">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">💬 Quotation Bank</h2>
                  <div className="space-y-3">
                    {generatedEssay.quotes.map((quote, idx) => (
                      <div key={idx} className="border-l-4 border-amber-400 pl-4">
                        <p className="text-sm italic text-slate-700">"{quote}"</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    );
  }

  // Evaluation Mode
  if (mode === "evaluate") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">⭐ Essay Evaluation</h1>
          <Button variant="outline" onClick={() => setMode("home")}>← Back</Button>
        </div>

        <Card>
          <CardContent className="p-6 space-y-4">
            {/* Input Mode Toggle */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Answer Input Method</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={!essayInputMode || essayInputMode === "type" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEssayInputMode("type")}
                  className={!essayInputMode || essayInputMode === "type" ? "bg-rose-500 hover:bg-rose-600" : ""}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Type Essay
                </Button>
                <Button
                  type="button"
                  variant={essayInputMode === "handwritten" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEssayInputMode("handwritten")}
                  className={essayInputMode === "handwritten" ? "bg-indigo-500 hover:bg-indigo-600" : ""}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Upload Handwritten
                </Button>
              </div>
            </div>

            {(!essayInputMode || essayInputMode === "type") ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Essay Topic</label>
                  <Input
                    placeholder="Enter the essay topic"
                    value={evaluationTopic}
                    onChange={(e) => setEvaluationTopic(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Your Essay</label>
                  <Textarea
                    placeholder="Paste your essay here (1000-1200 words recommended)"
                    value={userEssay}
                    onChange={(e) => setUserEssay(e.target.value)}
                    rows={20}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Word count: {userEssay.split(/\s+/).filter(w => w).length} words
                  </p>
                </div>

                {!isPro && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-xs text-amber-800">
                      {!evalUsage.free_full_evaluation_used 
                        ? "✨ Your first essay evaluation will be FULL with complete score breakdown and improvement suggestions!"
                        : "🔒 Full evaluation with score breakdown, detailed feedback, and improved intro/conclusion is PRO-only. You'll get partial evaluation with key feedback points."}
                    </p>
                  </div>
                )}

                <Button className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600" onClick={handleEvaluateEssay} disabled={loading}>
                  {loading ? "Evaluating..." : (!isPro && evalUsage.free_full_evaluation_used ? "Get Partial Evaluation" : "Evaluate Essay")}
                  <Award className="w-4 h-4 ml-2" />
                </Button>
              </>
            ) : (
              <HandwrittenUpload
                marks={125}
                suite="ESSAY"
                onEvaluationComplete={(data) => {
                  // Build a unified evaluation object so the rich UI below
                  // renders the same sections for typed essays and handwritten
                  // uploads.
                  const numericScore = parseInt(data.score_estimate);
                  const grade = Number.isFinite(numericScore)
                    ? (numericScore > 100 ? "A" : numericScore > 80 ? "B" : numericScore > 60 ? "C" : "D")
                    : "N/A";
                  setEvaluation({
                    total_score: data.score_estimate ?? "N/A",
                    grade,
                    // Use the 5-dim handwritten rubric scores directly
                    scores: data.scores || {},
                    // The handwritten rubric is /10 each — supply matching maxes
                    score_maxes: {
                      content_accuracy: 10,
                      structure_organization: 10,
                      depth_of_analysis: 10,
                      use_of_examples: 10,
                      language_presentation: 10,
                    },
                    strongest_areas: data.strongest_areas || [],
                    weakest_areas: data.weakest_areas || [],
                    structure_feedback: data.structure_feedback,
                    depth_feedback: data.depth_feedback,
                    missing_elements: data.missing_elements,
                    improvement_plan: data.improvement_plan,
                    rewrite_suggestions: data.rewrite_suggestions,
                    better_intro: data.better_intro,
                    better_conclusion: data.better_conclusion,
                    predicted_band: data.predicted_band,
                    balance_check: data.balance_check,
                    word_count: data.word_count,
                    real_ca_citations: data.real_ca_citations,
                    intelligence: data.intelligence,
                  });
                  if (data.extracted_question) {
                    setEvaluationTopic(data.extracted_question);
                  }
                  if (data.extracted_answer) {
                    setUserEssay(data.extracted_answer);
                  }
                  toast({ description: "✅ Handwritten essay evaluated!" });
                }}
                onExtractedData={(data) => {
                  if (data.question) setEvaluationTopic(data.question);
                  if (data.answer) setUserEssay(data.answer);
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Evaluation Results */}
        {evaluation && (() => {
          // Predicted band styling
          const band = evaluation.predicted_band || {};
          const bandStyles = {
            TOP_10_PERCENT: { pill: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Top 10% of essays', icon: '🏆' },
            ABOVE_CUTOFF:   { pill: 'bg-teal-100 text-teal-800 border-teal-300',           label: 'Above cutoff',     icon: '✅' },
            BORDERLINE:     { pill: 'bg-amber-100 text-amber-800 border-amber-300',        label: 'Borderline',       icon: '⚠️' },
            BELOW_CUTOFF:   { pill: 'bg-red-100 text-red-800 border-red-300',              label: 'Below cutoff',     icon: '❌' },
          };
          const bandStyle = bandStyles[band.label] || bandStyles.BORDERLINE;

          // Word count diagnostic (UPSC essay 1000–1200 ideal)
          const wc = evaluation.word_count || (userEssay ? userEssay.trim().split(/\s+/).length : 0);
          let wcStatus = 'OPTIMAL', wcColor = 'bg-emerald-100 text-emerald-800', wcNote = `${wc} words — Optimal length`;
          if (wc < 800)        { wcStatus = 'TOO_SHORT'; wcColor = 'bg-red-100 text-red-800';      wcNote = `${wc} words — Significantly below ideal (1000–1200)`; }
          else if (wc < 1000)  { wcStatus = 'SHORT';     wcColor = 'bg-amber-100 text-amber-800'; wcNote = `${wc} words — Below ideal (target 1000+)`; }
          else if (wc <= 1200) { wcStatus = 'OPTIMAL';   wcColor = 'bg-emerald-100 text-emerald-800'; wcNote = `${wc} words — Optimal length`; }
          else if (wc <= 1400) { wcStatus = 'LONG';      wcColor = 'bg-amber-100 text-amber-800'; wcNote = `${wc} words — Slightly over (1000–1200 ideal)`; }
          else                 { wcStatus = 'TOO_LONG';  wcColor = 'bg-red-100 text-red-800';      wcNote = `${wc} words — Too long; risk of repetition/padding`; }

          // Default score maxes for the 7-dim essay rubric
          const defaultMaxes = {
            structure: 20, content_depth: 25, examples_data: 20,
            coherence: 20, language: 20,
            constitutional_values: 10, overall: 10,
          };
          const maxes = { ...defaultMaxes, ...(evaluation.score_maxes || {}) };

          const triggerPracticeRewrite = (section) => {
            // Switch back to write mode with the rewrite scaffold pre-loaded
            const scaffold = evaluation.rewrite_suggestions?.[section]
              || (section === 'introduction' ? evaluation.better_intro
                : section === 'conclusion' ? evaluation.better_conclusion
                : '');
            if (!scaffold) {
              toast({ description: "No rewrite scaffold available for this section." });
              return;
            }
            setUserEssay(`${userEssay}\n\n--- Practice rewrite (${section}) ---\n${scaffold}\n\n--- Now rewrite this in your own voice ---\n`);
            toast({ description: `Scaffold loaded — rewrite the ${section} in your own voice, then re-evaluate.` });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          };

          return (
          <div className="space-y-4">
            {/* Score Card */}
            <Card className="border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50">
              <CardContent className="p-8">
                <div className="text-center">
                  <Award className="w-12 h-12 text-rose-600 mx-auto mb-4" />
                  <p className="text-5xl font-bold text-rose-900 mb-2" data-testid="essay-total-score">{evaluation.total_score}/125</p>
                  <Badge className="text-lg px-4 py-1">{evaluation.grade}</Badge>
                  <p className="text-sm text-slate-600 mt-4">UPSC Evaluation Score</p>

                  {/* Word count diagnostic */}
                  <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-2">
                    <Badge
                      data-testid="essay-word-count-badge"
                      className={`text-xs ${wcColor} border-0`}
                    >
                      📝 {wcNote}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Predicted Exam Band */}
            {band.label && (
              <Card className={`border-2 ${bandStyle.pill.replace('text-', 'border-').split(' ')[2] || 'border-slate-200'}`} data-testid="essay-predicted-band">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{bandStyle.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Predicted exam band</span>
                        <Badge className={`text-xs border ${bandStyle.pill}`} variant="outline">{bandStyle.label}</Badge>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{band.one_liner || 'Based on UPSC essay scoring norms across recent years.'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {evaluation.intelligence && (
              <IntelligencePanel
                intelligence={evaluation.intelligence}
                gaps={{
                  examples: evaluation.missing_elements?.examples,
                  keywords: evaluation.missing_elements?.keywords,
                  data: evaluation.missing_elements?.data,
                }}
              />
            )}

            {/* Detailed Scores — with maxes */}
            {evaluation.scores && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">📊 Detailed Scores</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(evaluation.scores).map(([criterion, score]) => {
                      const max = maxes[criterion] ?? null;
                      const numScore = Number(score) || 0;
                      const pct = max ? Math.round((numScore / max) * 100) : null;
                      const barColor = pct == null ? 'bg-slate-400'
                        : pct >= 80 ? 'bg-emerald-500'
                        : pct >= 60 ? 'bg-teal-500'
                        : pct >= 40 ? 'bg-amber-500'
                        : 'bg-red-500';
                      return (
                        <div key={criterion} className="p-3 bg-slate-50 rounded" data-testid={`essay-score-${criterion}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-slate-700 capitalize">
                              {criterion.replace(/_/g, " ").replace('examples data', 'examples & data')}
                            </span>
                            <Badge>{score}{max ? `/${max}` : ''}</Badge>
                          </div>
                          {max && (
                            <div className="h-1.5 bg-white rounded overflow-hidden">
                              <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Balance check */}
            {evaluation.balance_check && evaluation.balance_check.is_balanced === false && evaluation.balance_check.missing_perspective && (
              <Card className="border-orange-200 bg-orange-50" data-testid="essay-balance-check">
                <CardContent className="p-5">
                  <h3 className="text-sm font-bold text-orange-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Balance check — one-sided treatment
                  </h3>
                  <p className="text-sm text-orange-800">{evaluation.balance_check.missing_perspective}</p>
                  <p className="text-xs text-orange-700 mt-2 italic">UPSC reviewers reward essays that present counter-arguments and balanced perspectives.</p>
                </CardContent>
              </Card>
            )}

            {/* Strengths & Weaknesses */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-green-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Strongest Areas
                  </h2>
                  <ul className="space-y-2">
                    {evaluation.strongest_areas?.map((area, idx) => (
                      <li key={idx} className="text-sm text-green-800">✓ {area}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Weakest Areas
                  </h2>
                  <ul className="space-y-2">
                    {evaluation.weakest_areas?.map((area, idx) => (
                      <li key={idx} className="text-sm text-amber-800">⚠ {area}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Missing Elements — merged into the Knowledge Graph panel above */}

            {/* Improvement Plan */}
            {evaluation.improvement_plan && (
              <Card className="border-indigo-200 bg-indigo-50">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-indigo-900 mb-4">🎯 10-Point Improvement Plan</h2>
                  <ol className="space-y-2">
                    {evaluation.improvement_plan.map((point, idx) => (
                      <li key={idx} className="text-sm text-slate-700">{point}</li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}

            {/* Rewrite Suggestions — now intro + body + conclusion + practice CTAs */}
            {evaluation.rewrite_suggestions && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-4">✏️ Rewrite Suggestions</h2>

                  {evaluation.rewrite_suggestions.introduction && (
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-purple-700">Improved Introduction</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="essay-practice-intro"
                          onClick={() => triggerPracticeRewrite('introduction')}
                          className="text-xs h-7"
                        >
                          Practice rewrite →
                        </Button>
                      </div>
                      <p className="text-sm text-slate-700 bg-purple-50 p-3 rounded">
                        {evaluation.rewrite_suggestions.introduction}
                      </p>
                    </div>
                  )}

                  {evaluation.rewrite_suggestions.body && (
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-purple-700">Improved Body Paragraph</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="essay-practice-body"
                          onClick={() => triggerPracticeRewrite('body')}
                          className="text-xs h-7"
                        >
                          Practice rewrite →
                        </Button>
                      </div>
                      <p className="text-sm text-slate-700 bg-purple-50 p-3 rounded whitespace-pre-line">
                        {evaluation.rewrite_suggestions.body}
                      </p>
                    </div>
                  )}

                  {evaluation.rewrite_suggestions.key_paragraph && !evaluation.rewrite_suggestions.body && (
                    <div className="mb-5">
                      <h3 className="text-sm font-semibold text-purple-700 mb-2">Rewritten Key Paragraph</h3>
                      <p className="text-sm text-slate-700 bg-purple-50 p-3 rounded whitespace-pre-line">
                        {evaluation.rewrite_suggestions.key_paragraph}
                      </p>
                    </div>
                  )}

                  {evaluation.rewrite_suggestions.conclusion && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-purple-700">Improved Conclusion</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="essay-practice-conclusion"
                          onClick={() => triggerPracticeRewrite('conclusion')}
                          className="text-xs h-7"
                        >
                          Practice rewrite →
                        </Button>
                      </div>
                      <p className="text-sm text-slate-700 bg-purple-50 p-3 rounded">
                        {evaluation.rewrite_suggestions.conclusion}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          );
        })()}
      </div>
    );
  }

  return null;
};

export default EssaySuitePage;
